<template>
  <FormKit v-model="formData" type="form" @submit="onSave">
    <FormStepInputs
      :step="{
        stepName: name,
        formInputs: content,
      }"
    >
      <template #updateAddressMessageForBoth>
        <div
          v-if="showUpdateAddressMessageFor === 'updateAddressMessageForBoth'"
          class="flex md:w-176 items-start gap-4"
        >
          <InformationBox
            :title="updateAddressMessageForBoth.title.value"
            :description="updateAddressMessageForBoth.content.value"
            :type="updateAddressMessageForBoth.messageType.value"
            :isCircle="true"
            :useButtonSlot="false"
          ></InformationBox>
        </div>
      </template>
      <template #updateAddressMessageForBank>
        <div
          v-if="showUpdateAddressMessageFor === 'updateAddressMessageForBank'"
          class="flex md:w-176 items-start gap-4"
        >
          <InformationBox
            :title="updateAddressMessageForBank.title.value"
            :description="updateAddressMessageForBank.content.value"
            :type="updateAddressMessageForBank.messageType.value"
            :isCircle="true"
            :useButtonSlot="false"
          ></InformationBox>
        </div>
      </template>
      <template #updateAddressMessageForInsurance>
        <div
          v-if="showUpdateAddressMessageFor === 'updateAddressMessageForInsurance'"
          class="flex md:w-176 items-start gap-4"
        >
          <InformationBox
            :title="updateAddressMessageForInsurance.title.value"
            :description="updateAddressMessageForInsurance.content.value"
            :type="updateAddressMessageForInsurance.messageType.value"
            :isCircle="true"
            :useButtonSlot="false"
          ></InformationBox>
        </div>
      </template>
      <template #internationalAddressMessage>
        <div v-if="showInternationAddressMessage" class="flex md:w-51 items-start gap-4 mt-8">
          <InformationBox
            :title="internationalAddressMessage.title.value"
            :description="internationalAddressMessage.content.value"
            :type="internationalAddressMessage.messageType.value"
            :isCircle="false"
            :hideIcon="true"
            :useButtonSlot="false"
          ></InformationBox>
        </div>
      </template>
      <template #residentialAddress>
        <div
          v-if="
            enteredResidentialAddress &&
            (enteredResidentialAddress?.country?.length ?? 0) >= 3 &&
            enteredResidentialAddress?.country?.toLowerCase() !== AUSTRALIA_ISO.toLowerCase() &&
            enteredResidentialAddress?.country?.toLowerCase() !== AUSTRALIA.toLowerCase()
          "
          class="md:w-176 items-start gap-4 mt-4"
        >
          <label
            id="input_40_label"
            class="formkit-label block text-base-bold text-racq-dark-grey mb-1 text-input-label input-xl sm:pe-8"
            for="input_40"
            >{{ residentialAddress.label.value }}
            <button
              id="residentialAddressTooltipBtn"
              type="button"
              class="questionmark-icon js-tooltip-trigger -mt-0.5 inline-block"
              aria-label="show tooltip"
              @click="
                (e) => {
                  showTooltipOnTriggerClick(e, residentialAddress.tooltipContent?.value ?? '');
                }
              "
            ></button>
          </label>
          <FormKit
            id="residentialreadonly"
            v-model="displayOnlyResidentialAddress"
            innerClass="input-xl"
            :disabled="true"
            type="autocomplete"
            autocomplete="off"
            maxLength="255"
            listboxButtonClass="$reset hidden"
            loaderIconClass="$reset hidden"
          />
        </div>
        <div v-else class="mt-8 mb-8 md:mb-6">
          <AddressLookupOptions
            ref="residentialAddressEl"
            :instanceIdentifier="residentialIdentifier"
            v-bind="residentialAddress"
            :defaultIsOverseasOption="false"
            @manualAddressSelected="
              (address: Partial<RACQAddressResult>) => onResidentialManualAddressSelected(address)
            "
            @addressSelected="(address: Partial<RACQAddressResult>) => onResidentialAddressSelected(address)"
          />
        </div>
      </template>
      <template #invalidResidentialMessage>
        <div v-if="showInvalidResidentialMessage" class="flex md:w-[23rem] items-start gap-4 mt-4">
          <InformationBox
            :title="invalidResidentialMessage.title.value"
            :description="invalidResidentialMessage.content.value"
            :type="invalidResidentialMessage.messageType.value"
            :isCircle="false"
            :hideIcon="false"
            :useButtonSlot="false"
          ></InformationBox>
        </div>
      </template>
      <template #postalAddress>
        <div
          v-if="
            enteredPostalAddress &&
            (enteredPostalAddress?.country?.length ?? 0) >= 3 &&
            enteredPostalAddress?.country?.toLowerCase() !== AUSTRALIA_ISO.toLowerCase() &&
            enteredPostalAddress?.country?.toLowerCase() !== AUSTRALIA.toLowerCase()
          "
          class="md:w-176 items-start gap-4 mt-4"
        >
          <label
            id="postalreadonly_label"
            class="formkit-label block text-base-bold text-racq-dark-grey mb-1 text-input-label input-xl sm:pe-8"
            for="postalreadonly"
            >{{ postalAddress.label.value }}
            <button
              id="postalAddressTooltipBtn"
              type="button"
              class="questionmark-icon js-tooltip-trigger -mt-0.5 inline-block"
              aria-label="show tooltip"
              @click="
                (e) => {
                  showTooltipOnTriggerClick(e, postalAddress.tooltipContent?.value ?? '');
                }
              "
            ></button>
          </label>
          <FormKit
            id="postalreadonly"
            v-model="displayOnlyPostalAddress"
            :bindings="postalAddress"
            innerClass="input-xl"
            :disabled="true"
            type="autocomplete"
            autocomplete="off"
            maxLength="255"
            listboxButtonClass="$reset hidden"
            loaderIconClass="$reset hidden"
          />
        </div>
        <div v-else class="mt-4">
          <AddressLookupOptions
            ref="postalAddressEl"
            :instanceIdentifier="postalIdentifier"
            v-bind="postalAddress"
            :defaultIsOverseasOption="false"
            @manualAddressSelected="(address: Partial<RACQAddressResult>) => onPostalManualAddressSelected(address)"
            @addressSelected="(address: Partial<RACQAddressResult>) => onPostalAddressSelected(address)"
          />
        </div>
      </template>
      <template #invalidPostalMessage>
        <div v-if="showInvalidPostalMessage" class="flex md:w-[23rem] items-start gap-4 mt-4">
          <InformationBox
            :title="invalidPostalMessage.title.value"
            :description="invalidPostalMessage.content.value"
            :type="invalidPostalMessage.messageType.value"
            :isCircle="false"
            :hideIcon="false"
            :useButtonSlot="false"
          ></InformationBox>
        </div>
      </template>
    </FormStepInputs>
    <div class="flex w-[640px] mt-12 items-start content-start gap-4 flex-wrap">
      <Link :field="discardChangesLink?.link" class="btn btn-tertiary btn-md">
        <div class="flex items-center space-x-2">
          <img src="@/assets/icons/arrow-left.svg" alt="left arrow" class="w-5 h-5 inline-block" />
          <span>{{ discardChangesLink?.link?.value?.text }}</span>
        </div>
      </Link>
      <button :disabled="!hasChanges" type="submit" class="btn btn-md btn-primary">
        {{ getFormInput(props.content, 'saveButtonText')?.label?.value ?? 'Save changes' }}
      </button>
    </div>
    <div class="!mt-10 mb-10 md:!mt-12 md:!mb-12">
      <Divider></Divider>
    </div>
    <FullScreenSpinner v-if="showLoadingOrSaving" :applyOpacity="true"></FullScreenSpinner>
  </FormKit>
</template>
<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 } from 'uuid';
import { ISitecoreFormGenericInput, RACQAddressResult } from '@/interfaces';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { getFormInput } from '@/utils/form-input-helper';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import AddressLookupOptions from '@/components/AddressLookupOptions/AddressLookupOptions.vue';
import { useAccountStore, useAddressLookupStore, useAnalyticsStore } from '@/store';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { cloneDeep } from 'lodash-es';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import { INameValue } from '@/interfaces/entities/name-value.interface';
import { AUSTRALIA, AUSTRALIA_ISO } from '@/constants/general-words.const';
import { showTooltipOnTriggerClick } from '@/plugins/tooltip/renderTooltip';
import { FormKit } from '@formkit/vue';
import { FormEvents } from '@/enums/form-events.enum';
import Divider from '@/components/Divider/Divider.vue';
import { Link } from '@sitecore-jss/sitecore-jss-vue';
import {
  mapAddressResultToIdentityAddress,
  mapIdentityAddressToAddressResult,
} from '@/mappers/identity-address.mapper';
import { Address } from '@/interfaces';

const formData = ref<Record<string, any>>({});

const accountStore = useAccountStore();
const toastAlertStore = useToastAlertStore();
const analyticsStore = useAnalyticsStore();

const residentialIdentifier = v4();
const residentialAddressEl = ref<InstanceType<typeof AddressLookupOptions>>();
const residentialAddressStore = computed(() => residentialAddressEl.value?.getStore());

const postalIdentifier = v4();
const postalAddressEl = ref<InstanceType<typeof AddressLookupOptions>>();
const postalAddressStore = computed(() => postalAddressEl.value?.getStore());

const enteredResidentialAddress = ref<Partial<RACQAddressResult>>();
const enteredPostalAddress = ref<Partial<RACQAddressResult>>();
const showInternationAddressMessage = ref<boolean>(false);

const displayOnlyResidentialAddress = ref<string>('');
const displayOnlyPostalAddress = ref<string>('');

const showInvalidResidentialMessage = ref<boolean>(false);
const showInvalidPostalMessage = ref<boolean>(false);

const disableDiscardButton = ref<boolean>(false);
const hasChanges = ref<boolean>(false);

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();

const internationalAddressMessage = ref(getFormInput(props.content, 'internationalAddressMessage'));
const updateAddressMessageForBoth = ref(getFormInput(props.content, 'updateAddressMessageForBoth'));
const updateAddressMessageForBank = ref(getFormInput(props.content, 'updateAddressMessageForBank'));
const updateAddressMessageForInsurance = ref(getFormInput(props.content, 'updateAddressMessageForInsurance'));
const residentialAddress = ref(getFormInput(props.content, 'residentialAddress'));
const postalAddress = ref(getFormInput(props.content, 'postalAddress'));

const invalidResidentialMessage = ref(getFormInput(props.content, 'invalidResidentialMessage'));
const invalidPostalMessage = ref(getFormInput(props.content, 'invalidPostalMessage'));
const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));

const showLoadingOrSaving = ref<boolean>(true);
const countries = ref<any[]>([]);
const showUpdateAddressMessageFor = ref<string>();

const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

onMounted(async () => {
  await setAddresses();
  showLoadingOrSaving.value = false;

  const hasBanking = (accountStore.identityDetails?.portfolio?.bankingProducts?.length ?? 0) > 0;
  const hasInsurance = (accountStore.identityDetails?.portfolio?.insurancePolicies?.length ?? 0) > 0;

  if (hasBanking && hasInsurance) {
    showUpdateAddressMessageFor.value = 'updateAddressMessageForBoth';
  } else if (hasBanking) {
    showUpdateAddressMessageFor.value = 'updateAddressMessageForBank';
  } else if (hasInsurance) {
    showUpdateAddressMessageFor.value = 'updateAddressMessageForInsurance';
  }

  pushAnalytics();

  //Fixes styling issue when page content is short
  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.classList.add('grow-0');
  }
});

const buildAddressLine = async (address: RACQAddressResult, isPostal: boolean, addressStore: any) => {
  if ((countries.value?.length ?? 0) === 0) {
    countries.value = (await lookupCountry()) ?? [];
  }

  const country = countries.value.find((x) => x.value === address.country);

  if (isPostal) {
    if (enteredPostalAddress.value) {
      displayOnlyPostalAddress.value =
        addressStore?.buildInternationalAddressLine(enteredPostalAddress.value, country?.label) ?? '';
    }
  } else if (enteredResidentialAddress.value) {
    displayOnlyResidentialAddress.value =
      addressStore?.buildInternationalAddressLine(enteredResidentialAddress.value, country?.label) ?? '';
  }
};

const lookupCountry = async () => {
  try {
    if (postalAddressStore?.value) {
      const tempStore = useAddressLookupStore();
      const data: INameValue[] = await tempStore.getCountryLookup('', true, true);
      return (
        data?.map((item: INameValue) => ({
          label: item.name,
          value: item.value,
        })) || []
      );
    }
  } catch {
    return [];
  }
};

const setAddresses = async () => {
  let residentialIsOs = false;
  let postalIsOs = false;

  enteredResidentialAddress.value = cloneDeep(
    accountStore.identityDetails?.addresses?.find((x) => x.addressType === 'Residential')
  );

  if (enteredResidentialAddress.value) {
    enteredResidentialAddress.value = mapIdentityAddressToAddressResult(enteredResidentialAddress.value);
  }

  residentialAddressStore.value?.setupAddressComponent(
    {
      isPostalAddress: false,
      allowOverseasAddress: false,
      allowPhysicalAddressesOnly: true,
      showPostalSameAsInsuredAddressSelection: false,
      manualAddressConfirmationOnChangeOnly: true,
    },
    enteredResidentialAddress.value,
    enteredResidentialAddress.value
  );
  if (residentialAddressStore.value) residentialAddressStore.value.showDividerForManualAddress = true;

  if (
    enteredResidentialAddress?.value &&
    (enteredResidentialAddress?.value?.country?.length ?? 0) >= 3 &&
    enteredResidentialAddress?.value.country?.toLowerCase() !== AUSTRALIA_ISO.toLowerCase()
  ) {
    residentialIsOs = true;
    showInternationAddressMessage.value = true;
    await buildAddressLine(enteredResidentialAddress.value, false, residentialAddressStore.value);
  }

  enteredPostalAddress.value = cloneDeep(
    accountStore.identityDetails?.addresses?.find((x) => x.addressType === 'Postal')
  );

  //Mapping this to handle PO box so it displays correct.
  if (enteredPostalAddress.value) {
    enteredPostalAddress.value = mapIdentityAddressToAddressResult(enteredPostalAddress.value);
  }

  postalAddressStore.value?.setupAddressComponent(
    {
      isPostalAddress: true,
      allowOverseasAddress: false,
      showPostalSameAsInsuredAddressSelection: false,
      manualAddressConfirmationOnChangeOnly: true,
    },
    enteredPostalAddress.value,
    enteredPostalAddress.value
  );

  if (
    enteredPostalAddress?.value &&
    (enteredPostalAddress?.value?.country?.length ?? 0) >= 3 &&
    enteredPostalAddress?.value.country?.toLowerCase() !== AUSTRALIA_ISO.toLowerCase()
  ) {
    postalIsOs = true;
    showInternationAddressMessage.value = true;
    await buildAddressLine(enteredPostalAddress.value, true, postalAddressStore.value);
  }

  disableDiscardButton.value = residentialIsOs && postalIsOs;
};

function onResidentialAddressSelected(address: Partial<RACQAddressResult>) {
  hasChanges.value = true;
  enteredResidentialAddress.value = address;
}

function onResidentialManualAddressSelected(address: Partial<RACQAddressResult>) {
  hasChanges.value = true;
  enteredResidentialAddress.value = address;
}

function onPostalAddressSelected(address: Partial<RACQAddressResult>) {
  hasChanges.value = true;
  enteredPostalAddress.value = address;
}

function onPostalManualAddressSelected(address: Partial<RACQAddressResult>) {
  hasChanges.value = true;
  enteredPostalAddress.value = address;
}

const onSave = async () => {
  showInvalidResidentialMessage.value = residentialAddressStore.value?.manualEditEnabled ?? false;
  showInvalidPostalMessage.value = postalAddressStore.value?.manualEditEnabled ?? false;

  if (showInvalidResidentialMessage.value || showInvalidPostalMessage.value) return;

  if (!enteredResidentialAddress.value || !enteredPostalAddress.value) {
    toastAlertStore.addToastAlert({
      title: 'Not all addresses have been completed',
      description: '',
      type: ToastAlertType.ERROR,
      duration: 6000,
    });

    return;
  }

  showLoadingOrSaving.value = true;

  try {
    const isResidentialAusAddress =
      enteredResidentialAddress.value.country?.toLowerCase() === AUSTRALIA.toLowerCase() ||
      enteredResidentialAddress.value.country?.toLowerCase() === AUSTRALIA_ISO.toLowerCase();
    const updateResidentialAddress = isResidentialAusAddress ? enteredResidentialAddress.value : undefined;

    const isPostalAusAddress =
      enteredPostalAddress.value.country?.toLowerCase() === AUSTRALIA.toLowerCase() ||
      enteredPostalAddress.value.country?.toLowerCase() === AUSTRALIA_ISO.toLowerCase();
    const updatePostalAddress = isPostalAusAddress ? enteredPostalAddress.value : undefined;

    const residentialAddress = mapAddressResultToIdentityAddress(updateResidentialAddress ?? {}, false);
    const postalAddress = mapAddressResultToIdentityAddress(updatePostalAddress ?? {}, true);

    hasChanges.value = false;

    await accountStore.updateAddressInformation(
      accountStore.identityDetails?.d365Id ?? '',
      handleValidAddress(residentialAddress),
      handleValidAddress(postalAddress)
    );

    await accountStore.fetchIdentityDetails(true);

    showLoadingOrSaving.value = false;

    toastAlertStore.addToastAlert({
      title: 'Account addresses updated',
      description: '',
      type: ToastAlertType.SUCCESS,
      duration: 6000,
    });

    await analyticsStore.pushAnalytics({
      event: FormEvents.COMPLETE,
      form: {
        name: 'Change profile address',
        stepName: 'FormCompleted',
      },
    });
  } catch (error) {
    showLoadingOrSaving.value = false;
    toastAlertStore.addToastAlert({
      title: 'Unable to save changes',
      description: '',
      type: ToastAlertType.ERROR,
      duration: 6000,
    });

    await analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Change profile address',
        formErrorType: 'Unable to save changes',
        formErrorDetail: 'Unable to save changes',
      },
    });
  }
};

//When an international address exists it can't be edited so we want to pass undefined for that address
//to prevent the server from trying to update it.
const handleValidAddress = (address: Address) => {
  if ((address?.street?.length ?? 0) === 0 || (address?.suburb?.length ?? 0) === 0) {
    return undefined;
  }

  return address;
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);
  pageAnalytics.pushAnalytics();
};
</script>
